import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateCashoutLog } from 'modules/wallet/earnings.actions';
import { CashoutFooter } from './cashoutFooter';
import { Row, NumericalValue, Button } from 'modules/styled';
import { useTx, useSRel, useWeb3React } from 'web3/hooks';
import { TX_STATUS } from 'web3/utils';
import {
  VerifyEmail,
  renderWarnings,
  Progress,
  ProgressType,
  useVerifyEmail,
  ClaimProps,
  useClaimVest,
  useUnclaimedVest,
} from './claimUtils';
import { alert } from 'utils';

const Alert = alert.Alert();

export const VestHandler = ({ canClaim, close }: ClaimProps) => {
  const [token, setToken] = useState('');
  const [earning, setEarning] = useState();
  const { account } = useWeb3React();

  const user = useSelector((state: any) => state.auth.user);
  const unclaimedVest = useUnclaimedVest(account as string | undefined);

  const { sendTx, txStatus, error } = useTx();
  const sRel = useSRel();
  const pendingTx = txStatus === TX_STATUS.PENDING;

  const [amount, setAmount] = useState(user.unvestedTokens);
  const dispatch = useDispatch();

  useEffect(() => {
    if (unclaimedVest) setAmount(unclaimedVest.shortVest + unclaimedVest.longVest);
  }, [unclaimedVest]);

  const { verifyEmail, sendEmail, sendingEmail, emailError } = useVerifyEmail();
  const { claimUnvestedTokens, claimingVest, claimError } = useClaimVest();

  const startClaim = async () => {
    const confirm = window.confirm(
      `WARNING: You have to pay Ethereum gas fees to complete this transaction (up to $30 USD).\n\nOnce you press OK, you cannot cancel this transaction. If the blockchain transaction fails or you hit 'Reject', you will need to repeat the same transaction to redeem your tokens.`
    );
    if (!confirm) return;
    if (user.email && user.confirmed && !token) return verifyEmail();
    return cashOut();
  };

  const cashOut = async () => {
    try {
      const {
        data: { claimVest = undefined },
      } = await claimUnvestedTokens({ variables: { token } });
      const { shortVestBN, longVestBN, signature, earning } = claimVest;
      if (!claimVest) {
        return Alert.alert('There was an error getting claim signature', 'error');
      }
      if (!sRel) {
        return Alert.alert('Missing contract', 'error');
      }
      setEarning(earning);
      sendTx(sRel, 'initVesting', [shortVestBN, longVestBN, signature]);
    } catch (err) {
      if (err instanceof Error) Alert.alert(err?.message, 'error');
    }
  };

  useEffect(() => {
    if (error) Alert.alert(error, 'error');
  }, [error]);

  useEffect(() => {
    if (txStatus === TX_STATUS.CONFIRMED) {
      dispatch(updateCashoutLog(earning));
      close();
    }
  }, [txStatus, dispatch]);

  if (sendEmail && !txStatus)
    return (
      <VerifyEmail
        email={user.email}
        cashOut={cashOut}
        setToken={setToken}
        token={token}
      />
    );

  return (
    <Fragment>
      {renderWarnings(null, emailError)}
      <Row>
        <NumericalValue
          sx={{
            lineHeight: 6,
            flex: 1,
            mr: 1,
            fontSize: amount ? 4 : 2,
            px: 1.5,
            fontWeight: 'bold',
          }}
        >
          Claim {amount} REL
        </NumericalValue>
        <Button
          disabled={!amount || amount === ''}
          sx={{
            height: 8,
            mr: 'auto',
          }}
          onPress={startClaim}
        >
          Claim Unvested REL
        </Button>
      </Row>
      <CashoutFooter />
      {pendingTx || sendingEmail ? (
        <Progress
          type={sendingEmail && !pendingTx ? ProgressType.EMAIL : ProgressType.TX}
        />
      ) : null}
    </Fragment>
  );
};
